/**
 * OpenLayers 3.x Bridge Module
 * 
 * This module wraps the global `ol` object from lib/ol/ol.js (v3.15)
 * and provides ES6-style imports for TypeScript.
 * 
 * This is a temporary bridge to allow migration from global `ol.*` references
 * to proper imports, without upgrading to OL10 yet.
 * 
 * Usage:
 *   Instead of: new ol.Map({ ... })
 *   Use: import { Map } from './openlayers/ol3-bridge';
 *        new Map({ ... })
 */

// Import the local OpenLayers v3.15 file (sets up global ol object)
import 'ol-local';

// Import OpenLayers CSS
import '../../../lib/ol/ol.css';

// Declare the global ol object that was set up by lib/ol/ol.js
declare const ol: any;

// Safety check: only export if ol is defined (allows safe import when using OL10)
const olGlobal = typeof ol !== 'undefined' ? ol : null;

if (!olGlobal) {
    console.warn('[ol3-bridge] Global ol object not found. This is expected when using OpenLayers 10.');
}

// Re-export the global ol object components as named exports
// Use fallback empty objects if ol is not defined
export const Map = olGlobal?.Map;
export const View = olGlobal?.View;
export const Feature = olGlobal?.Feature;
export const Overlay = olGlobal?.Overlay;
export const Collection = olGlobal?.Collection;

// Layer types
export const layer = {
    Tile: olGlobal?.layer?.Tile,
    Vector: olGlobal?.layer?.Vector,
    Group: olGlobal?.layer?.Group,
    Base: olGlobal?.layer?.Base,
};

export const TileLayer = olGlobal?.layer?.Tile;
export const VectorLayer = olGlobal?.layer?.Vector;

// Source types
export const source = {
    OSM: olGlobal?.source?.OSM,
    XYZ: olGlobal?.source?.XYZ,
    Vector: olGlobal?.source?.Vector,
    Cluster: olGlobal?.source?.Cluster,
};

export const XYZ = olGlobal?.source?.XYZ;
export const VectorSource = olGlobal?.source?.Vector;
export const ClusterSource = olGlobal?.source?.Cluster;
export const TileGrid = olGlobal?.tilegrid?.TileGrid;

export const tilegrid = {
    TileGrid: olGlobal?.tilegrid?.TileGrid,
};

// Geometry types
export const geom = {
    Point: olGlobal?.geom?.Point,
    Polygon: olGlobal?.geom?.Polygon,
    LineString: olGlobal?.geom?.LineString,
    Circle: olGlobal?.geom?.Circle,
};

export const GeomPoint = olGlobal?.geom?.Point;
export const Polygon = olGlobal?.geom?.Polygon;
export const LineString = olGlobal?.geom?.LineString;

// Interaction utilities
export const interaction = {
    defaults: olGlobal?.interaction?.defaults,
    Draw: olGlobal?.interaction?.Draw,
    Modify: olGlobal?.interaction?.Modify,
    Select: olGlobal?.interaction?.Select,
};

export const Modify = olGlobal?.interaction?.Modify;

// Control utilities
export const control = {
    defaults: olGlobal?.control?.defaults,
    Control: olGlobal?.control?.Control,
    MousePosition: olGlobal?.control?.MousePosition,
};

export const Control = olGlobal?.control?.Control;
export const MousePosition = olGlobal?.control?.MousePosition;

// Projection utilities
export const proj = {
    transform: olGlobal?.proj?.transform,
    Projection: olGlobal?.proj?.Projection,
};

export const Projection = olGlobal?.proj?.Projection;

// Coordinate utilities
export const coordinate = {
    createStringXY: olGlobal?.coordinate?.createStringXY,
};

// Extent utilities
export const extent = {
    getCenter: olGlobal?.extent?.getCenter,
};

// Style utilities
export const style = {
    Style: olGlobal?.style?.Style,
    Icon: olGlobal?.style?.Icon,
    Fill: olGlobal?.style?.Fill,
    Stroke: olGlobal?.style?.Stroke,
    Text: olGlobal?.style?.Text,
    Circle: olGlobal?.style?.Circle,
};

export const Style = olGlobal?.style?.Style;
export const Icon = olGlobal?.style?.Icon;
export const Fill = olGlobal?.style?.Fill;
export const Stroke = olGlobal?.style?.Stroke;
export const Text = olGlobal?.style?.Text;
export const Circle = olGlobal?.style?.Circle;

// Format
export const format = {
    GeoJSON: olGlobal?.format?.GeoJSON,
};

export const GeoJSON = olGlobal?.format?.GeoJSON;

// ol.inherits function for custom controls
export const inherits = olGlobal?.inherits;

// Export the full ol namespace for gradual migration
// Note: This must be a const assignment, not a re-export
export const olNamespace = olGlobal;
